import { useCallback, useState, useEffect } from 'react';
import useColumnWidthsChange from 'src/componentes/list/utils/use-column-widths-change';
import { useIntl } from 'react-intl';

import { ParseError } from 'src/services/downloadFileUrl';
import ListColumn from 'src/componentes/NewList/ListColumn';
import Toolbar from 'src/componentes/NewList/Toolbar';
import Results from 'src/componentes/NewList/Results';
import Button from 'src/componentes/button';
import LoadingContainer from 'src/componentes/loading-container';
import {
  getColumns,
  getFilter,
  getData,
  getPermissions,
  exportList,
} from 'src/componentes/NewList';
import LucideIcon from 'src/componentes/lucide-icon';
import { COLUNAS, FILTROS } from 'src/utils/constants';
import { capitalizeFirstLetter } from 'src/utils/string';
import errorHandler from 'src/utils/error-handler';
import useAuth from 'src/hooks/useAuth';
import Actions from './Actions';
import Edit from '../EventoEditView';
import ContentManager from 'src/componentes/content-manager';
import formatDateFns from 'src/utils/formatDateFns';
import { format } from 'date-fns';
import { formatNumber } from 'src/utils/number';

const DEFAULT_PARAMETERS = {};

function EventoRiscoListView({
  columnsTag = COLUNAS.EVENTO_RISCO,
  filterTag = FILTROS.EVENTO_RISCO,
  entityTag = null,
  endpoint = '/EventoRisco',
  defaultSort = 'DataOcorrencia',
  defaultSortBy = 'desc',
  selectedFilter = null,
  openLastFilter = true,
  parameters = DEFAULT_PARAMETERS,
  configuracao,
}) {
  const intl = useIntl();

  const [updatedItems] = useState([]);
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [columns, setColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const handleColumnWidthsChange = useColumnWidthsChange(
    setColumns,
    setAllColumns
  );
  const [permissions, setPermissions] = useState({});
  const [filter, setFilter] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [sortField, setSortField] = useState(defaultSort);
  const [sortFieldBy, setSortFieldBy] = useState(defaultSortBy);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');

  const { terms: resources, user } = useAuth();
  const { currentDisplay, currentLocale } = formatDateFns(user?.idioma, true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const newPermissions = await getPermissions(endpoint, parameters);
      setPermissions(newPermissions);

      let newData = [];

      if (newPermissions.allowView || newPermissions.allowList) {
        await loadColumns();

        let newFilter = selectedFilter;
        if (openLastFilter && filterTag)
          newFilter = await getFilter(filterTag, true);

        setFilter(newFilter);
        let valueFilter = {};
        if (newFilter != null) valueFilter = newFilter.valor;

        const model = {
          ...valueFilter,
          searchText,
        };
        newData = await getData(
          endpoint,
          model,
          sortField,
          sortFieldBy,
          pageSize,
          currentPage - 1,
          parameters
        );
        setData(newData.list);
        setCount(newData.count);
      }
      setLoading(false);
      return newData;
    } catch (err) {
      setLoading(false);
      errorHandler(err);
    }
  }, [
    endpoint,
    parameters,
    searchText,
    pageSize,
    currentPage,
    sortField,
    sortFieldBy,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData, pageSize, currentPage, searchText, sortField, sortFieldBy]);

  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
  });

  const handleColumnsChange = useCallback(async () => {
    await loadColumns();
    await loadData();
  });

  const handleRefresh = useCallback(async () => {
    await loadData();
  });

  const handleChangeCurrentPage = useCallback((newValue) => {
    setCurrentPage(newValue);
  });

  const handleChangePageSize = useCallback((pageSize) => {
    setPageSize(pageSize);
  });

  const handleSortColumn = useCallback((sort) => {
    setSortField(sort);
  });

  const handleSortByColumn = useCallback((sortBy) => {
    setSortFieldBy(sortBy);
  });

  const handleExport = async (fileExtension) => {
    try {
      setLoading(true);

      let valueFilter = {};
      if (filter != null) valueFilter = filter.valor;

      let newSearchModel = {
        searchText,
        ...valueFilter,
      };

      await exportList(
        newSearchModel,
        endpoint,
        sortField,
        currentPage,
        parameters,
        fileExtension
      );
      setLoading(false);
    } catch (err) {
      setLoading(false);
      errorHandler(ParseError(err));
    }
  };

  const handleNew = () =>
    ContentManager.addContent(
      <Edit
        handleClose={handleRefresh}
        configuracao={configuracao}
        parameters={parameters}
      />
    );

  const handleEdit = (item) =>
    ContentManager.addContent(
      <Edit
        item={item}
        handleClose={handleRefresh}
        configuracao={configuracao}
        parameters={parameters}
      />
    );

  const renderImpacto = (item) => {
    if (!item.impacto) return;
    return item.impacto?.nome;
  };

  const renderResposta = (item) => {
    if (!item.respostaOcorrencia) return;
    return item.respostaOcorrencia?.nome;
  };

  const renderDescricao = (item) => {
    if (!item?.descricao) return '-';

    if (!item?.permissions?.allowEdit) return item.descricao;

    return (
      <a
        href="# "
        className="text-primary"
        onClick={(event) => {
          event.preventDefault();
          handleEdit(item);
        }}
      >
        {item.descricao}
      </a>
    );
  };

  const renderActions = (item) => (
    <Actions
      onRefresh={handleRefresh}
      item={item}
      endpoint={endpoint}
      handleOpenEdit={handleEdit}
    />
  );

  const renderDate = ({ dataOcorrencia }) =>
    dataOcorrencia &&
    format(dataOcorrencia, currentDisplay, { locale: currentLocale });

  const defaultColumns = [
    <ListColumn
      key="idIntegracao"
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'idIntegracao' })
      )}
      valueField="idIntegracao"
      default
    />,
    <ListColumn
      key="dataOcorrencia"
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'dataOcorrencia' })
      )}
      valueField="dataOcorrencia"
      default
      sortField="dataOcorrencia"
      valueFunction={renderDate}
    />,
    <ListColumn
      key="descricao"
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'descricaoEvento' })
      )}
      valueField="descricao"
      valueFunction={renderDescricao}
      default
      sortField="descricao"
    />,
    <ListColumn
      key="impacto"
      headerText={capitalizeFirstLetter(intl.formatMessage({ id: 'impacto' }))}
      valueField="impacto"
      default
      sortField="impacto"
      valueFunction={renderImpacto}
      visible={configuracao.avaliarImpacto}
    />,
    <ListColumn
      key="impactoFinanceiro"
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'labelImpactoFinanceiro' })
      )}
      valueField="impactoFinanceiro"
      valueFunction={(item) =>
        item.impactoFinanceiro ? (
          <div style={{ minWidth: 100 }}>
            {configuracao?.unidadeMedidaImpactoFinanceiro?.sigla}{' '}
            {formatNumber(item.impactoFinanceiro, 2)}
          </div>
        ) : (
          <>-</>
        )
      }
      default
    />,
    <ListColumn
      key="resposta"
      headerText={capitalizeFirstLetter(intl.formatMessage({ id: 'resposta' }))}
      valueField="resposta"
      valueFunction={renderResposta}
      default
      visible={configuracao.utilizarCampoResposta}
    />,
  ];

  async function loadColumns() {
    try {
      const currentColumns = await getColumns(
        defaultColumns,
        entityTag,
        columnsTag
      );
      setColumns(currentColumns.columns);
      setAllColumns(currentColumns.allColumns);
    } catch (err) {
      errorHandler(err);
    }
  }

  const actions = !permissions?.allowEdit
    ? []
    : [
        <Button size="icon" key={1} className="tw:ms-2" onClick={handleNew}>
          <LucideIcon icon="plus" />
        </Button>,
      ];

  return (
    <LoadingContainer isLoading={isLoading}>
      <Toolbar
        useSavedFilter
        showSearch
        onExport={handleExport}
        onColumnsChange={handleColumnsChange}
        onSearchChange={handleSearchChange}
        onRefresh={handleRefresh}
        searchText={searchText}
        columnsTag={columnsTag}
        columns={columns}
        allColumns={allColumns}
        actions={actions}
      />
      <Results
        columnsTag={columnsTag}
        onColumnsChange={handleColumnWidthsChange}
        resources={resources}
        count={count}
        columns={columns}
        data={data}
        currentPage={currentPage}
        pageSize={pageSize}
        onChangeCurrentPage={handleChangeCurrentPage}
        onChangePageSize={handleChangePageSize}
        onSortColumn={handleSortColumn}
        onSortByColumn={handleSortByColumn}
        sort={sortField}
        sortBy={sortFieldBy}
        updatedItems={updatedItems}
        parameters={parameters}
        showMenu
        renderMenu={renderActions}
        handleRefreshPage={handleRefresh}
      />
    </LoadingContainer>
  );
}

export default EventoRiscoListView;
